import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { gameApi, type GameConfigDto, type StageProgressDto } from '../api/game';
import { stageEnemyLabel, stageHint } from '../lib/presentation';

export function StagesPage() {
  const [config, setConfig] = useState<GameConfigDto | null>(null);
  const [progress, setProgress] = useState<StageProgressDto | null>(null);
  const [chapter, setChapter] = useState(1);
  const [message, setMessage] = useState('正在读取服务端主线进度…');

  useEffect(() => {
    Promise.allSettled([gameApi.getConfig(), gameApi.getStageProgress()]).then(([configResult, progressResult]) => {
      if (configResult.status === 'fulfilled') setConfig(configResult.value);
      if (progressResult.status === 'fulfilled') {
        setProgress(progressResult.value);
        if (configResult.status === 'fulfilled') {
          setChapter(configResult.value.stages.find(stage => stage.stageNumber === progressResult.value.highestUnlockedStage)?.chapterId ?? 1);
        }
      }
      const failure = progressResult.status === 'rejected' ? progressResult.reason : configResult.status === 'rejected' ? configResult.reason : null;
      setMessage(failure instanceof Error ? failure.message : failure ? '读取失败。' : '');
    });
  }, []);

  const items = useMemo(() => config?.stages.filter(stage => stage.chapterId === chapter) ?? [], [config, chapter]);
  const chapters = useMemo(() => [...new Map((config?.stages ?? []).map(stage => [stage.chapterId, {
    id: stage.chapterId,
    name: stage.chapterName,
    first: stage.stageNumber,
    last: stage.stageNumber,
  }])).values()].map(item => {
    const chapterStages = config?.stages.filter(stage => stage.chapterId === item.id) ?? [];
    return { ...item, first: Math.min(...chapterStages.map(stage => stage.stageNumber)), last: Math.max(...chapterStages.map(stage => stage.stageNumber)) };
  }), [config]);
  const progressByStage = new Map(progress?.stages.map(item => [item.stageNumber, item]) ?? []);

  return <section className="page-card stages-page" aria-labelledby="page-title">
    <div className="page-heading"><div><p className="eyebrow">{chapters.length || '—'} 章 · {config?.stages.length ?? '—'} 关</p><h1 id="page-title">主线闯关</h1></div><p>不设体力与虚假战力门槛，是否解锁由服务端进度决定。</p></div>
    <div className="chapter-tabs" role="tablist">{chapters.map(item => <button key={item.id} className={chapter === item.id ? 'active' : ''} onClick={() => setChapter(item.id)}><strong>{item.name}</strong><small>第 {item.first}—{item.last} 关</small></button>)}</div>
    <p className="status-line" role="status">{message}</p>
    {!progress && <div className="state-panel"><strong>{config ? '关卡进度尚未同步' : '正在加载三章关卡'}</strong><span>{config ? '不会把未知进度显示为未解锁，也不会使用本地假数据。' : '关卡名称与奖励将从服务端配置读取。'}</span><button onClick={() => window.location.reload()}>重新加载</button></div>}
    {progress && <div className="stage-list">{items.map(stage => {
      const state = progressByStage.get(stage.stageNumber);
      const unlocked = state?.unlocked ?? false;
      const enemyTypes = [...new Set(stage.waves.flatMap(wave => wave.groups.map(group => group.enemyType)))];
      const className = !unlocked ? 'locked' : state?.highestStars === 3 ? 'three-star' : state?.isCleared ? 'cleared' : 'available';
      return <article key={stage.stageNumber} className={className}>
        <div className="stage-number"><span>{stage.isBoss ? '首领关' : `第 ${stage.stageNumber} 关`}</span><strong>{stage.stageNumber.toString().padStart(2, '0')}</strong></div>
        <div className="stage-copy"><div><h2>{stage.name}</h2><span className="stage-state">{!unlocked ? '未解锁' : state?.highestStars === 3 ? '已三星' : state?.isCleared ? `已通关 · ${state.highestStars}星` : '可挑战'}</span></div><p>{stageHint(enemyTypes)} · {stage.waveCount} 波</p><div className="enemy-tags">{enemyTypes.map(type => <span key={type}>{stageEnemyLabel(type)}</span>)}</div></div>
        <div className="stage-rewards"><span>首通 <b>{config?.rewards.firstClearKing ?? '—'} KING</b> {state?.firstClearRewardGranted ? '已发放' : '未发放'}</span><span>首三星 <b>{config?.rewards.firstThreeStarKing ?? '—'} KING</b> {state?.firstThreeStarRewardGranted ? '已发放' : '未发放'}</span></div>
        {unlocked ? <Link className="compact-cta" to={`/battle?stage=${stage.stageNumber}`}>{state?.isCleared ? '再次挑战' : '开始挑战'} →</Link> : <button className="compact-cta" disabled>尚未解锁</button>}
      </article>;
    })}</div>}
  </section>;
}
